import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Share, PlusSquare, Download } from 'lucide-react';

const DISMISS_KEY = 'feriasclube_install_dismissed_at';
const DISMISS_DAYS = 14;

const isStandalone = () => {
  try {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  } catch {
    return false;
  }
};

const isIOS = () => {
  try {
    return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
  } catch {
    return false;
  }
};

const wasDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const dismissedAt = Number(raw);
    if (!dismissedAt) return false;
    const days = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
    return days < DISMISS_DAYS;
  } catch {
    return false;
  }
};

const markDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // ignora — pior caso o banner volta a aparecer
  }
};

/** Banner leve pra instalar o app na tela inicial — mostra o botão nativo no
 * Android/Chrome (via beforeinstallprompt) e o passo a passo manual no iPhone
 * (Safari não expõe nenhum evento/API pra isso). */
const InstallAppPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSSteps, setShowIOSSteps] = useState(false);
  const [visible, setVisible] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    if (isIOS()) {
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    markDismissed();
    setVisible(false);
    setShowIOSSteps(false);
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {
        // usuário fechou o prompt nativo — sem problema
      }
      setDeferredPrompt(null);
      setVisible(false);
      return;
    }
    setShowIOSSteps(true);
  };

  if (!visible || pathname === '/cortesia' || pathname === '/cortesia/confirmacao') return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9998] px-3 pb-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
      <div className="mx-auto max-w-md rounded-2xl shadow-lg overflow-hidden" style={{ backgroundColor: '#0E1B2C' }}>
        {!showIOSSteps ? (
          <div className="flex items-center gap-3 p-3">
            <img src="/logo192.png" alt="FériasClube" className="w-11 h-11 rounded-xl flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-white m-0 truncate">Instalar o app FériasClube</p>
              <p className="text-xs m-0 truncate" style={{ color: 'rgba(255,255,255,0.65)' }}>Acesso rápido, direto da tela inicial</p>
            </div>
            <button type="button" onClick={handleInstallClick}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold"
              style={{ backgroundColor: '#F5C04A', color: '#063847' }}>
              <Download className="w-3.5 h-3.5" /> Instalar
            </button>
            <button type="button" onClick={dismiss} aria-label="Fechar"
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white m-0">Como instalar no iPhone</p>
              <button type="button" onClick={dismiss} aria-label="Fechar"
                className="w-7 h-7 rounded-full flex items-center justify-center" style={{ color: 'rgba(255,255,255,0.6)' }}>
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-2.5 text-sm" style={{ color: 'rgba(255,255,255,0.85)' }}>
              <div className="flex items-center gap-2.5">
                <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                  <Share className="w-3.5 h-3.5" />
                </span>
                Toque no ícone de compartilhar do Safari
              </div>
              <div className="flex items-center gap-2.5">
                <span className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                  <PlusSquare className="w-3.5 h-3.5" />
                </span>
                Escolha "Adicionar à Tela de Início"
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallAppPrompt;
